import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import { POST_LOGIN_OTP, POST_LOGIN_OTP_VERIFY } from "../../config/endpoints";
import { handleLoginError, handleOtpError } from '../../config/errors';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import 'flag-icons/css/flag-icons.min.css';
import PreSignDialog from "../PreSign/PreSignDialog";
import ReactGA from 'react-ga4';
import { SERVER_MESSAGE } from "../../config/settings";

const LoginPage = ({ onCancelDialog }) => {
    const navigate = useNavigate();

    // GA: page view for Login dialog/page
    useEffect(() => {
        try { ReactGA.send({ hitType: 'pageview', page: '/login', title: 'Login' }); } catch { }
    }, []);

    // State management
    const [showOTPBox, setShowOTPBox] = useState(false);
    const [otp, setOTP] = useState(["", "", "", ""]);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [timer, setTimer] = useState(30);
    const [mobileNumber, setMobileNumber] = useState("");
    const [countryCode, setcountryCode] = useState("+91");
    const otpInputs = useRef(Array(4).fill(null));
    const [signupOTP, setSignupOTP] = useState(false);
    const [loading, setLoading] = useState(false);
    const [redirectionPath, setRedirectionPath] = useState('/');
    const [showPreSign, setShowPreSign] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);

    // Country selector states
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [countrySearch, setCountrySearch] = useState("");
    const [filteredCountries, setFilteredCountries] = useState([]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (otpInputs.current[0]) {
            otpInputs.current[0].focus();
        }
    }, [showOTPBox]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        otpInputs[0]?.current?.focus();
    }, [signupOTP]);

    // Global keyboard listener for OTP input when OTP box is shown
    useEffect(() => {
        if (!showOTPBox) return;

        const handleGlobalKeyPress = (e) => {
            // Only handle numeric keys
            if (!/^\d$/.test(e.key)) return;

            // Prevent default to avoid typing in other inputs
            e.preventDefault();

            // Find the first empty OTP box
            const emptyIndex = otp.findIndex(digit => digit === '');

            if (emptyIndex !== -1) {
                // Fill the empty box
                const newOTP = [...otp];
                newOTP[emptyIndex] = e.key;
                setOTP(newOTP);

                // Focus the next box if available
                if (emptyIndex < 3 && otpInputs.current[emptyIndex + 1]) {
                    otpInputs.current[emptyIndex + 1].focus();
                }
            }
        };

        // Add event listener
        document.addEventListener('keydown', handleGlobalKeyPress);

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleGlobalKeyPress);
        };
    }, [showOTPBox, otp]);

    // Country selector functions
    // Cache the highly expensive Intl.DisplayNames allocations globally
    let cachedCountriesData = null;
    const getCountriesData = () => {
        if (cachedCountriesData) return cachedCountriesData;
        const countryList = getCountries();
        const preferredCountries = ['US', 'GB', 'IN', 'CA', 'AU', 'DE', 'FR', 'JP', 'SG', 'NL'];

        const sortedCountries = countryList.sort((a, b) => {
            const aPreferred = preferredCountries.includes(a);
            const bPreferred = preferredCountries.includes(b);

            if (aPreferred && !bPreferred) return -1;
            if (!aPreferred && bPreferred) return 1;

            return a.localeCompare(b);
        });

        cachedCountriesData = sortedCountries.map(country => ({
            code: country,
            name: new Intl.DisplayNames(['en'], { type: 'region' }).of(country) || country,
            dialCode: `+${getCountryCallingCode(country)}`,
            isPreferred: preferredCountries.includes(country)
        }));
        return cachedCountriesData;
    };

    const getCountryFlagClass = (countryCode) => {
        return `fi fi-${countryCode.toLowerCase()}`;
    };

    const getCountryCodeFromDialCode = (dialCode) => {
        const country = filteredCountries.find(c => c.dialCode === dialCode);
        if (country) {
            return country.code;
        }

        const allCountries = getCountriesData();
        const foundCountry = allCountries.find(c => c.dialCode === dialCode);

        return foundCountry ? foundCountry.code : 'IN';
    };

    // Filter countries based on search
    useEffect(() => {
        if (countrySearch.trim() === "") {
            setFilteredCountries(getCountriesData());
        } else {
            const allCountries = getCountriesData();
            const filtered = allCountries.filter(country =>
                country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                country.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
                country.dialCode.includes(countrySearch)
            );
            setFilteredCountries(filtered);
        }
    }, [countrySearch]);

    // Initialize with default country data
    useEffect(() => {
        const initialCountries = getCountriesData();
        setFilteredCountries(initialCountries);

        // Set default country code to India (+91)
        if (!countryCode || countryCode === '+1') {
            const defaultCountry = initialCountries.find(c => c.code === 'IN');
            if (defaultCountry) {
                setcountryCode(defaultCountry.dialCode);
            }
        }
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const countrySelector = event.target.closest('.country-selector-wrapper');
            if (showCountryDropdown && !countrySelector) {
                setShowCountryDropdown(false);
                setCountrySearch("");
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCountryDropdown]);

    // Keyboard navigation support
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (showCountryDropdown) {
                if (event.key === 'Escape') {
                    setShowCountryDropdown(false);
                    setCountrySearch("");
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showCountryDropdown]);

    let intervalId;

    useEffect(() => {
        if (resendDisabled) {
            intervalId = setInterval(() => {
                setTimer((prevTimer) => {
                    if (prevTimer === 0) {
                        clearInterval(intervalId);
                        setResendDisabled(false);
                        return 30;
                    } else {
                        return prevTimer - 1;
                    }
                });
            }, 1000);
        }
        return () => clearInterval(intervalId);
    }, [resendDisabled]);

    const handleSendOTP = () => {
        try { ReactGA.event({ category: 'Login', action: 'send_otp_click' }); } catch { }

        const rules = getPhoneValidationRules(countryCode);
        if (!rules.pattern.test(mobileNumber)) {
            toast.error(rules.message, {
                duration: 4000,
                style: {
                    background: '#fff',
                    color: '#1f2937',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #fee2e2',
                },
            });
            return;
        }

        setLoading(true);
        var obj = {
            "type": signupOTP ? 'registration' : 'login',
            "phone": {
                "cc": countryCode,
                "number": mobileNumber
            }
        };

        var apiUrl = new URL(POST_LOGIN_OTP);
        fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(obj)
        }).then(res => {
            return res.json();
        }).then(response => {
            console.log("LOGIN-OTP RESPONSE: ", response);
            const errorMsg = SERVER_MESSAGE ? response.message : handleOtpError(response);

            if (response && 'code' in response && response['code'] == 200) {
                try { ReactGA.event({ category: 'Login', action: 'send_otp_success' }); } catch { }
                if (signupOTP) {
                    localStorage.setItem('SIGN_UP_BQS__ID', response?.data["_id"]);
                    setRedirectionPath("/signup");
                    toast.success(errorMsg || "OTP sent! Redirecting to sign up page.", {
                        duration: 2000,
                        style: {
                            background: '#fff',
                            color: '#059669',
                            borderRadius: '12px',
                            padding: '16px',
                            border: '1px solid #d1fae5',
                        },
                    });
                    setTimeout(() => {
                        navigate(redirectionPath);
                        onCancelDialog();
                    }, 1000);
                } else {
                    setShowOTPBox(true);
                    setResendDisabled(true);
                    toast.success(errorMsg || "OTP sent successfully!", {
                        duration: 3000,
                        style: {
                            background: '#fff',
                            color: '#059669',
                            borderRadius: '12px',
                            padding: '16px',
                            border: '1px solid #d1fae5',
                        },
                    });
                }
            } else {
                console.log("Error while generating OTP: ", response);
                toast.error(errorMsg || "Something went wrong", {
                    duration: 4000,
                    style: {
                        background: '#fff',
                        color: '#dc2626',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid #fee2e2',
                    },
                });
                try { ReactGA.event({ category: 'Login', action: 'send_otp_error', label: String(response?.code || '') }); } catch { }
            }
        }).catch(error => {
            toast.error(error.message || "Something went wrong", {
                duration: 4000,
                style: {
                    background: '#fff',
                    color: '#dc2626',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #fee2e2',
                },
            });
            try { ReactGA.event({ category: 'Login', action: 'send_otp_error', label: error?.message || 'network' }); } catch { }
        }).finally(() => setLoading(false));
    };

    const handleVerifyOTP = (otpOverride = null) => {
        try { ReactGA.event({ category: 'Login', action: 'verify_click' }); } catch { }
        let optInString = (otpOverride && Array.isArray(otpOverride)) ? otpOverride.join('') : otp.filter(Boolean).join("");

        if (!optInString) {
            toast.error("Please enter the OTP received on your mobile number", {
                duration: 3000,
                style: {
                    background: '#fff',
                    color: '#dc2626',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #fee2e2',
                },
            });
            return;
        } else if (optInString.length < 4) {
            toast.error("Please enter a valid OTP", {
                duration: 3000,
                style: {
                    background: '#fff',
                    color: '#dc2626',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #fee2e2',
                },
            });
            return;
        }

        setLoading(true);
        var obj = {
            "phone": {
                "cc": countryCode,
                "number": mobileNumber
            },
            "otp": optInString,
        };

        var apiUrl = new URL(POST_LOGIN_OTP_VERIFY);
        fetch(apiUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(obj)
        }).then(res => {
            return res.json();
        }).then(response => {
            console.log("LOGIN-OTP RESPONSE: ", response);
            const loginErrorMsg = SERVER_MESSAGE ? response.message : handleLoginError(response);

            if (response && 'code' in response && response['code'] == 200) {
                try { ReactGA.event({ category: 'Login', action: 'verify_success' }); } catch { }
                localStorage.setItem('BQS__ID', response.data["_id"]);
                localStorage.setItem('BQS_TOKEN', response.data.token.token);
                localStorage.setItem('BQS_ROLE', response.data.role);
                localStorage.setItem('BQS_USER_NAME', response.data.profileData.name.firstName);

                const membershipProfile = response?.data?.profileData?.membershipProfile;
                // Strict check: Only Players are redirected to the pricing page if they lack an active plan.
                // Coaches are always redirected to their dashboard regardless of subscription status.
                const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
                const isEnforced = enforcement === 'hard';
                const hasActiveSubscription = !isEnforced || (membershipProfile && membershipProfile.isActive === true);

                const redirectPath = response?.data?.role === "coach" 
                    ? "/coach_flow/reviewd-video" 
                    : (hasActiveSubscription ? "/game-posts/game-videos" : "/subscription-pricing");
                    
                setRedirectionPath(redirectPath);

                toast.success(loginErrorMsg || "OTP Verified! You're now logged in.", {
                    duration: 2000,
                    icon: '🎉',
                    style: {
                        background: '#fff',
                        color: '#059669',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid #d1fae5',
                    },
                });

                setTimeout(() => {
                    navigate(redirectPath);
                    onCancelDialog();
                }, 1000);
            } else {
                console.log("Error >>>>");
                clearInterval(intervalId);
                setResendDisabled(true);
                toast.error(loginErrorMsg || `Oops! Something went wrong. Please try again later`, {
                    duration: 4000,
                    style: {
                        background: '#fff',
                        color: '#dc2626',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid #fee2e2',
                    },
                });
                try { ReactGA.event({ category: 'Login', action: 'verify_error', label: String(response?.code || '') }); } catch { }
            }
        }).catch(error => {
            toast.error(error.message || "Oops! Something went wrong. Please try again later", {
                duration: 4000,
                style: {
                    background: '#fff',
                    color: '#dc2626',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #fee2e2',
                },
            });
            try { ReactGA.event({ category: 'Login', action: 'verify_error', label: error?.message || 'network' }); } catch { }
        }).finally(() => setLoading(false));
    };

    const handleResendOTP = () => {
        handleSendOTP();
        setTimer(30);
        setResendDisabled(true);
        setOTP(["", "", "", ""]);
        try { ReactGA.event({ category: 'Login', action: 'resend_otp' }); } catch { }
    };

    const handleChangeMobileNumber = (e) => {
        const value = e.target.value;
        const rules = getPhoneValidationRules(countryCode);
        if (/^\d*$/.test(value) && value.length <= rules.maxLength) {
            setMobileNumber(value);
            setHasValue(value.length > 0);
        }
    };

    const handleOnKeyDownMobileNumber = (e) => {
        if (e.key === "Enter") {
            handleSendOTP();
        }
    };

    const handleChangeOTP = (index, value) => {
        if (!/^\d*$/.test(value)) {
            return;
        }
        const newOTP = [...otp];
        newOTP[index] = value;
        setOTP(newOTP);

        if (value !== '' && index < 3 && otpInputs.current[index + 1]) {
            otpInputs.current[index + 1].focus();
        }
    };

    const closeSignup = () => {
        setSignupOTP(!signupOTP);
    };

    const selectCountry = (code) => {
        const country = filteredCountries.find(c => c.code === code);
        if (country) {
            setcountryCode(country.dialCode);
        }
        setShowCountryDropdown(false);
        setCountrySearch("");
    };

    // Country-specific phone number validation rules
    const getPhoneValidationRules = (countryCode) => {
        const rules = {
            '+91': { minLength: 10, maxLength: 10, pattern: /^\d{10}$/, message: 'Indian phone number must be exactly 10 digits' },
            '+1': { minLength: 10, maxLength: 10, pattern: /^\d{10}$/, message: 'US/Canada phone number must be exactly 10 digits' },
            '+44': { minLength: 10, maxLength: 11, pattern: /^\d{10,11}$/, message: 'UK phone number must be 10-11 digits' },
            '+61': { minLength: 9, maxLength: 9, pattern: /^\d{9}$/, message: 'Australian phone number must be exactly 9 digits' },
            '+49': { minLength: 10, maxLength: 12, pattern: /^\d{10,12}$/, message: 'German phone number must be 10-12 digits' },
            '+33': { minLength: 9, maxLength: 10, pattern: /^\d{9,10}$/, message: 'French phone number must be 9-10 digits' },
            '+81': { minLength: 10, maxLength: 11, pattern: /^\d{10,11}$/, message: 'Japanese phone number must be 10-11 digits' },
            '+65': { minLength: 8, maxLength: 8, pattern: /^\d{8}$/, message: 'Singapore phone number must be exactly 8 digits' },
            '+31': { minLength: 9, maxLength: 9, pattern: /^\d{9}$/, message: 'Dutch phone number must be exactly 9 digits' },
            '+971': { minLength: 9, maxLength: 9, pattern: /^\d{9}$/, message: 'UAE phone number must be exactly 9 digits' }
        };

        return rules[countryCode] || {
            minLength: 7,
            maxLength: 15,
            pattern: /^\d{7,15}$/,
            message: `Phone number must be 7-15 digits for ${countryCode}`
        };
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0 && otpInputs.current[index - 1]) {
            otpInputs.current[index - 1].focus();
            const newOTP = [...otp];
            newOTP[index - 1] = '';
            setOTP(newOTP);
        } else if (e.key === 'Enter') {
            handleVerifyOTP();
        } else if (e.key === 'ArrowLeft' && index > 0 && otpInputs.current[index - 1]) {
            otpInputs.current[index - 1].focus();
        } else if (e.key === 'ArrowRight' && index < 3 && otpInputs.current[index + 1]) {
            otpInputs.current[index + 1].focus();
        }
    };

    // Handle paste in OTP inputs
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 4);
        if (/^\d+$/.test(pastedData)) {
            const newOTP = pastedData.split('').concat(['', '', '', '']).slice(0, 4);
            setOTP(newOTP);
            if (pastedData.length === 4) {
                otpInputs.current[3]?.focus();
            } else if (pastedData.length > 0) {
                otpInputs.current[Math.min(pastedData.length, 3)]?.focus();
            }
        }
    };

    return (
        <>
            <Toaster position="top-center" />

            {!showPreSign && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 overflow-hidden z-[1300]">
                    <AnimatePresence mode="wait">
                        {!showOTPBox ? (
                            <motion.div
                                key="login"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.3 }}
                                className="w-full max-w-[440px] mx-auto relative z-10 flex items-center justify-center"
                            >
                                {/* Login Card */}
                                <div className="bg-white border border-gray-200 rounded-2xl p-8 sm:p-10 shadow-xl relative w-full">
                                    {/* Close Button */}
                                    <button
                                        onClick={onCancelDialog}
                                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-all"
                                        aria-label="Close"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>

                                    {/* Logo & Title */}
                                    {!signupOTP && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.1 }}
                                            className="text-center mb-8"
                                        >
                                            <div className="flex items-center justify-center gap-3 mb-6">
                                                <img src="/logo192.png" alt="VISIST.AI" className="h-12 w-12" />
                                                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">VISIST.AI</h1>
                                            </div>
                                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
                                            <p className="text-gray-600 text-base">Sign in with your Mobile number to continue your journey</p>
                                        </motion.div>
                                    )}

                                    {/* Phone Number Input */}
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.15 }}
                                        className="space-y-5"
                                    >
                                        {/* Input Label */}
                                        <div>
                                            <div className="flex gap-2 sm:gap-3 justify-center">
                                                {/* Country Selector */}
                                                <div className="country-selector-wrapper relative">
                                                    <button
                                                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                                        className="h-12 px-3 bg-gray-50 border border-gray-300 rounded-xl hover:border-emerald hover:bg-gray-100 transition-all flex items-center justify-center gap-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald/50"
                                                        type="button"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className={`${getCountryFlagClass(getCountryCodeFromDialCode(countryCode))} text-lg`} />
                                                            <span className="font-medium text-sm">{countryCode}</span>
                                                        </div>
                                                        <svg className={`w-3.5 h-3.5 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>

                                                    {/* Country Dropdown */}
                                                    <AnimatePresence>
                                                        {showCountryDropdown && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                                                transition={{ duration: 0.2 }}
                                                                className="absolute top-full left-0 mt-2 w-80 max-h-96 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-50"
                                                            >
                                                                {/* Search */}
                                                                <div className="p-3 border-b border-gray-200">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Search countries..."
                                                                        value={countrySearch}
                                                                        onChange={(e) => setCountrySearch(e.target.value)}
                                                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald/50"
                                                                        autoFocus
                                                                    />
                                                                </div>

                                                                {/* Countries List */}
                                                                <div className="max-h-80 overflow-y-auto">
                                                                    {filteredCountries.length === 0 ? (
                                                                        <div className="p-4 text-center text-gray-500 text-sm">
                                                                            No countries found
                                                                        </div>
                                                                    ) : (
                                                                        filteredCountries.map((country) => (
                                                                            <button
                                                                                key={country.code}
                                                                                onClick={() => selectCountry(country.code)}
                                                                                className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors ${countryCode === country.dialCode ? 'bg-emerald/10' : ''
                                                                                    }`}
                                                                                type="button"
                                                                            >
                                                                                <span className={`${getCountryFlagClass(country.code)} text-lg`} />
                                                                                <div className="flex-1 text-left">
                                                                                    <div className="text-gray-900 font-medium text-sm">{country.name}</div>
                                                                                    <div className="text-gray-500 text-xs">{country.code}</div>
                                                                                </div>
                                                                                <span className="text-gray-700 text-sm">{country.dialCode}</span>
                                                                                {countryCode === country.dialCode && (
                                                                                    <svg className="w-4 h-4 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                                    </svg>
                                                                                )}
                                                                            </button>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>

                                                {/* Phone Input */}
                                                <input
                                                    ref={inputRef}
                                                    type="tel"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Enter mobile number"
                                                    value={mobileNumber}
                                                    onChange={handleChangeMobileNumber}
                                                    onKeyDown={handleOnKeyDownMobileNumber}
                                                    onFocus={() => setIsFocused(true)}
                                                    onBlur={() => setIsFocused(false)}
                                                    maxLength={getPhoneValidationRules(countryCode).maxLength}
                                                    className="flex-1 h-12 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald/50 focus:border-emerald hover:bg-gray-100 transition-all text-center"
                                                />
                                            </div>

                                        </div>

                                        {/* Privacy Notice */}
                                        <p className="text-xs text-gray-500 text-center leading-relaxed">
                                            By signing up, you agree to our{' '}
                                            <Link to="/privacy_policy" className="text-emerald hover:text-emerald-600 underline font-medium">
                                                Privacy Notice
                                            </Link>
                                        </p>

                                        {/* Send OTP Button */}
                                        <motion.button
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.99 }}
                                            onClick={handleSendOTP}
                                            disabled={loading}
                                            className="w-full h-12 bg-emerald hover:bg-emerald-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald/20 hover:shadow-emerald/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    <span>Sending...</span>
                                                </>
                                            ) : (
                                                'Send OTP'
                                            )}
                                        </motion.button>

                                        {/* Sign Up Link */}
                                        {signupOTP ? (
                                            <p className="text-center text-gray-600 text-sm">
                                                Already have an account?{' '}
                                                <button onClick={closeSignup} className="text-emerald hover:text-emerald-600 font-semibold">
                                                    Login
                                                </button>
                                            </p>
                                        ) : (
                                            <p className="text-center text-gray-600 text-sm">
                                                Don't have an account?{' '}
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        try { ReactGA.event({ category: 'Login', action: 'signup_presign_open' }); } catch { }
                                                        setShowPreSign(true);
                                                    }}
                                                    className="text-emerald hover:text-emerald-600 font-semibold"
                                                >
                                                    Sign Up
                                                </button>
                                            </p>
                                        )}
                                    </motion.div >
                                </div >
                            </motion.div >
                        ) : (
                            // OTP Verification Screen
                            <motion.div
                                key="otp"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.3 }}
                                className="w-full max-w-[440px] mx-auto relative z-10 flex items-center justify-center"
                            >
                                <div className="bg-white border border-gray-200 rounded-2xl p-8 sm:p-10 shadow-xl relative w-full">
                                    {/* Close Button */}
                                    <button
                                        onClick={onCancelDialog}
                                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-all"
                                        aria-label="Close"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>

                                    {/* Header */}
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.1 }}
                                        className="text-center mb-8"
                                    >
                                        <div className="w-16 h-16 bg-emerald/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <svg className="w-8 h-8 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Number</h2>
                                        <p className="text-gray-600 text-sm">
                                            Enter the OTP sent to{' '}
                                            <span className="text-gray-900 font-semibold">
                                                {countryCode} {mobileNumber}
                                            </span>
                                        </p>
                                        <button
                                            onClick={() => setShowOTPBox(false)}
                                            className="text-emerald hover:text-emerald-600 text-sm mt-2 font-medium"
                                        >
                                            Change number
                                        </button>
                                    </motion.div>

                                    {/* OTP Inputs */}
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                        className="space-y-6"
                                    >
                                        <div className="flex gap-3 justify-center">
                                            {otp.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    ref={(el) => (otpInputs.current[index] = el)}
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="\d*"
                                                    maxLength={1}
                                                    value={digit}
                                                    onChange={(e) => handleChangeOTP(index, e.target.value)}
                                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                                    onPaste={index === 0 ? handlePaste : undefined}
                                                    style={{ WebkitTextSecurity: 'disc' }}
                                                    className="w-12 h-12 text-center text-2xl font-bold bg-gray-50 border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald focus:border-emerald transition-all"
                                                />
                                            ))}
                                        </div>

                                        {/* Resend OTP */}
                                        <div className="text-center">
                                            {resendDisabled ? (
                                                <div className="flex items-center justify-center gap-2 text-gray-600">
                                                    <div className="relative w-8 h-8">
                                                        <svg className="w-8 h-8 transform -rotate-90">
                                                            <circle
                                                                cx="16"
                                                                cy="16"
                                                                r="14"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                fill="none"
                                                                className="text-gray-200"
                                                            />
                                                            <circle
                                                                cx="16"
                                                                cy="16"
                                                                r="14"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                fill="none"
                                                                strokeDasharray={`${(timer / 30) * 88} 88`}
                                                                className="text-emerald transition-all duration-1000"
                                                            />
                                                        </svg>
                                                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-900">
                                                            {timer}
                                                        </span>
                                                    </div>
                                                    <span className="text-sm">Resend OTP in {timer}s</span>
                                                </div>
                                            ) : (
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={handleResendOTP}
                                                    className="text-emerald hover:text-emerald-600 font-semibold text-sm"
                                                >
                                                    Resend OTP
                                                </motion.button>
                                            )}
                                        </div>

                                        {/* Verify Button */}
                                        <motion.button
                                            type="button"
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.99 }}
                                            onClick={handleVerifyOTP}
                                            disabled={loading}
                                            className="w-full h-12 bg-emerald hover:bg-emerald-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald/20 hover:shadow-emerald/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    <span>Verifying...</span>
                                                </>
                                            ) : (
                                                'Verify & Login'
                                            )}
                                        </motion.button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence >
                </div >
            )}

            <PreSignDialog
                open={showPreSign}
                onClose={() => setShowPreSign(false)}
                onSuccess={() => {
                    setShowPreSign(false);
                    navigate('/signup');
                    onCancelDialog && onCancelDialog();
                }}
            />
        </>
    );
};

export default LoginPage;
